
import React, { FC, useState, useCallback, useEffect } from 'react';
import {
    Badge,
    Menu,MenuButton,
    MenuTrigger,
    MenuPopover,
    MenuList,
    MenuItem,
    MenuDivider,  Link, 
    Tooltip,
    Button
} from '@fluentui/react-components';

import { PanelLeftClose, PanelRightClose, Download, LogOut, Sun, Moon, LaptopMinimal, PlusCircle } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useLocation } from 'react-router-dom';
import { useUserContext } from '../../Contexts/UserContext';
import { useTheme } from '../../store/hooks';
import './HeaderView.scss';
import SyButton from '../Common/SyButton';
import Skeleton from 'react-loading-skeleton';
import { useRootState } from '../../store/hooks';
import MenuPersona from './MenuPersona';
import { AppThemeMode, FORCED_DARK_THEME_MODE, FORCED_LIGHT_THEME_MODE, FROM_DEVICE_THEME_MODE } from '../../store/models/theme';

    
export interface HeaderProps {
    // onMenuToggle now receives a boolean indicating the desired "open" state (true = open, false = closed)
    onMenuToggle?: (open: boolean) => void;
    isMenuOpen?: boolean;
    onNewChatClick?: () => void;
    onVersionNumberClick?: () => void;
    environmentName?: string;
    onToggleTheme?: () => void;
    themeMode: AppThemeMode;
    handleInstallApp: () => void;
    handleLogout: () => void;
    userName?: string;
    userPhoto?: string;
}

export const HeaderView: FC<HeaderProps> = (props: HeaderProps) => {
    const { onMenuToggle, onNewChatClick, isMenuOpen, onVersionNumberClick, environmentName, onToggleTheme, themeMode, handleInstallApp,
        handleLogout, userName, userPhoto
    } = props;
    const { t } = useTranslation();
    const navigate = useNavigate();
    const location = useLocation();
    const { userProfile } = useUserContext();
    const { theme } = useTheme();
    const [isMobileOverlayOpen, setIsMobileOverlayOpen] = useState(false);
    const handleNavigate = useCallback((path: string) => {
        navigate(path);
        setIsMobileOverlayOpen(false);
    }, [navigate]);


    const { state } = useRootState();
    const appLoading = state.appLoading ?? false;
    const conversationsLoading = state.conversationsLoading ?? false;
    const loading = appLoading || conversationsLoading;
    if (process.env.NODE_ENV !== 'production') console.debug('[HeaderView] loading=', loading, 'appLoading=', appLoading, 'conversationsLoading=', conversationsLoading);

    if (loading) {
        return (
            <header className="header-view">
                {/* Mirror real header DOM structure so skeletons align exactly */}
                <div className="header-view__left">
                    
                    <div className="header-view__left__infoAppGroup">
                        <span className="header-view__left__logoAndAppName">
                            <Skeleton className="skeleton-rect-36" />
                            <Skeleton className="skeleton-brand" />
                        </span>
                    </div>
                </div>

                <div className="header-view__right">
                    <Skeleton className="skeleton-rect-36" />
                    <Skeleton className="skeleton-rect-36" />
                    <Skeleton className="skeleton-rect-36 ml-auto" />
                </div>
            </header>
        );
    }

    return (
        <>
            <header className="header-view">
                {/* Left Section - Logo SyGPT + Version + Environment */}
                <div className="header-view__left">
                    <SyButton appearance="tertiary"  className='themeButton' iconOnly icon={isMenuOpen ? <PanelLeftClose /> : <PanelRightClose />} onClick={() => onMenuToggle && onMenuToggle(!isMenuOpen)}></SyButton>
                    <div className="header-view__left__infoAppGroup" >
                        {/* Logo and Application Name */}
                        <span className="header-view__left__logoAndAppName" >
                            <img  src="Syensqo-Logo.svg" alt="Syensqo" onClick={onNewChatClick} ></img>
                            <span onClick={onNewChatClick} >{t('AppTitle')}</span>
                        </span>
                        {/* badges Version - Environnement */}
                        <Tooltip content={'Click to know more'} relationship='label'>
                            <Button appearance='transparent' size='small' className='version-btn' onClick={onVersionNumberClick}>
                                <Badge className="header-view__badge" >v{t('version')}</Badge>
                            </Button>
                        </Tooltip>
                        {environmentName && <Badge className="header-view__badgeEnvironment" > {environmentName} </Badge>}
                    </div>
                </div>

                {/* Right Section - Actions and Profile */}
                <div className="header-view__right">
                    <SyButton appearance="outline2" icon={<PlusCircle />} onClick={onNewChatClick}>
                        New chat
                    </SyButton>
                    {/* Dark/Light/DeviceControl */}
                    <SyButton appearance="tertiary" onClick={onToggleTheme} iconOnly className='themeButton'>
                        {themeMode === FORCED_LIGHT_THEME_MODE ? <Sun size={16} /> : themeMode === FORCED_DARK_THEME_MODE ? <Moon  size={16}/> : <LaptopMinimal  size={16}/>}
                    </SyButton>
                    {/* User Profile Menu */}
                    <Menu positioning="below-end">
                        <MenuTrigger disableButtonEnhancement>
                            <MenuButton className="header-view__profile">
                                <MenuPersona
                                    name={userName}
                                    photo={userPhoto}
                                />
                            </MenuButton>
                        </MenuTrigger>
                        <MenuPopover>
                            <MenuList className='header-view__profile__menu'>
                                {/* <MenuItem icon={<Download />} onClick={() => handleInstallApp()}>
                                    {t('header.install', 'Install SyGPT')}
                                </MenuItem>
                                <MenuDivider /> */}
                                <MenuItem  className='logOutMenuItem' icon={<LogOut />} onClick={() => handleLogout()} >
                                    {t('header.logout', 'Log out')}
                                </MenuItem>
                            </MenuList>
                        </MenuPopover>
                    </Menu>
                </div>
            </header>
        </>
    );
};
